import type { InjectedRouter } from 'react-router';
import { window } from '../../../globals';
import BannerManager from '../../BannerManager';
import CenteredLayout from '../layouts/Centered';
import AuthContextProvider, { useAuthContext } from '../context/AuthContext';
import ForgotPasswordLink from '../common/ForgotPasswordLink';
import SignInSignUpToggle from '../common/SignInSignUpToggle';
import AuthForm from '../form';
import { FormGroup } from '../../generic/Forms';
import type { RouterProps } from '../../../types';

/**
 * Page for logging into FOSSA.
 *
 * AuthContext manages the state for logging in, this is just the markup for that page.
 */
const Login = () => {
  const {
    email: {
      ref: emailInputRef,
      ...email
    },
    password: {
      ref: passwordInputRef,
      ...password
    },
    alert,
    status,
    passwordStrength,
    hidePasswordInput,
    isLdapAuthRequired,
    step,
    page,
    submitButton,
    loginMethods,
    inviteToken,
    samlURL,
  } = useAuthContext();
  return (
    <CenteredLayout>
      <AuthForm.Container onSubmit={submitButton.onSubmit}>
        <AuthForm.Heading heading="Sign In" />
        <AuthForm.Alert alert={alert} status={status} />
        <FormGroup>
          <AuthForm.EmailInput
            ref={emailInputRef}
            email={email}
            hidePasswordInput={hidePasswordInput}
            step={step}
            onSubmit={submitButton.onSubmit}
          />
          <AuthForm.PasswordInput
            ref={passwordInputRef}
            step={step}
            password={password}
            hidePasswordInput={hidePasswordInput}
            passwordStrength={passwordStrength}
            onSubmit={submitButton.onSubmit}
          />
          {!isLdapAuthRequired && (
            <ForgotPasswordLink />
          )}
        </FormGroup>
        {samlURL && (
          <AuthForm.SamlLoginButton
            samlURL={samlURL}
          />
        )}
        <AuthForm.SubmitButton
          page={page}
          step={step}
          status={status}
          submitButton={submitButton}
          hidePasswordInput={hidePasswordInput}
        />
        <AuthForm.LoginMethods
          loginMethods={loginMethods}
          hidePasswordInput={hidePasswordInput}
          inviteToken={inviteToken}
          page={page}
        />
      </AuthForm.Container>
      <SignInSignUpToggle
        page="login"
        email={email.value}
      />
      {window.on_prem && (
        <BannerManager />
      )}
    </CenteredLayout>
  );
};

type LoginPageProps = {
  location: RouterProps['location'];
  router: InjectedRouter;
};

/**
 * Login page for Auth 2.0
 */
const LoginPage = ({ location, router }: LoginPageProps) => (
  <AuthContextProvider location={location} router={router}>
    <Login />
  </AuthContextProvider>
);

export default LoginPage;
